import { LazyQueryHookOptions, QueryHookOptions } from "@apollo/client"

import { useEvent } from "shared/lib/useEvent"
import { Maybe } from "shared/types/Maybe"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"

import {
  ProductConfigurationQuery,
  ProductConfigurationQueryVariables
} from "entities/dim-configuration/graphql/product-configuration.queries.generated"
import { flattenProductConfigurationFormData } from "entities/dim-configuration/lib/flattenFormData"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"

import { useProductConfigurationQueryWithCacheLazy } from "./useProductConfigurationQueryWithCache"

function filterRemovedAddonsProperties(
  addons: ProductConfigurationFormData["addons"]
) {
  if (addons && typeof addons === "object") {
    return Object.fromEntries(
      Object.entries(addons).map(
        ([key, formData]: [string, ProductConfigurationFormData]): [
          string,
          ProductConfigurationFormData
        ] => [
          key,
          {
            value: formData.value,
            product: formData.value ? formData?.product : undefined,
            addons: formData.value
              ? filterRemovedAddonsProperties(formData?.addons)
              : undefined
          }
        ]
      )
    )
  }
  return addons
}

function filterRemovedAddons(
  nextFormData: ProductConfigurationFormData
): ProductConfigurationFormData
function filterRemovedAddons(nextFormData: null | undefined): null | undefined
function filterRemovedAddons(
  nextFormData: Maybe<ProductConfigurationFormData>
): Maybe<ProductConfigurationFormData>
function filterRemovedAddons(
  nextFormData: ProductConfigurationFormData | null | undefined
) {
  if (nextFormData?.addons && typeof nextFormData.addons === "object") {
    return {
      ...nextFormData,
      addons: filterRemovedAddonsProperties(nextFormData.addons)
    }
  }
  return nextFormData
}

export function querifyFormData(
  variables: UseProductConfigurationByFormDataQueryVariables
) {
  const { formData, lastModifiedAttribute } = variables
  const formDataParameters = formData
    ? flattenProductConfigurationFormData(filterRemovedAddons(formData))
    : {}

  if (lastModifiedAttribute) {
    formDataParameters.lastModifiedAttribute = lastModifiedAttribute
  }

  return encodeURIComponent(JSON.stringify(formDataParameters))
}

interface UseProductConfigurationByFormDataQueryVariables {
  productType: string
  formData: ProductConfigurationFormData
  lastModifiedAttribute?: string
  dimensioningKey?: string | null
}

interface UseProductConfigurationByFormDataArgs
  extends Omit<
    QueryHookOptions<
      ProductConfigurationQuery,
      ProductConfigurationQueryVariables
    >,
    "variables"
  > {
  variables: UseProductConfigurationByFormDataQueryVariables
}

interface ProductConfigurationByFormDataLazyFetchOptions
  extends Omit<
    Partial<
      LazyQueryHookOptions<
        ProductConfigurationQuery,
        ProductConfigurationQueryVariables
      >
    >,
    "variables"
  > {
  variables: UseProductConfigurationByFormDataQueryVariables
}

export function useProductConfigurationByFormDataLazy(
  baseOptions?: UseProductConfigurationByFormDataArgs
) {
  const [fetch, query] = useProductConfigurationQueryWithCacheLazy({
    ...baseOptions,
    variables: baseOptions?.variables
      ? getProductConfigurationQueryVariables({
          productType: baseOptions.variables.productType,
          formData: querifyFormData(baseOptions.variables),
          dimensioningKey: baseOptions.variables.dimensioningKey
        })
      : undefined
  })

  const handleFetch = useEvent(
    (options: ProductConfigurationByFormDataLazyFetchOptions) =>
      fetch({
        ...options,
        variables: getProductConfigurationQueryVariables({
          productType: options.variables?.productType,
          formData: querifyFormData(options.variables),
          dimensioningKey: options.variables.dimensioningKey
        })
      })
  )

  return [handleFetch, query] as [typeof handleFetch, typeof query]
}
